import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import LegalLayout from "@/components/LegalLayout";
import { COMPANY } from "@/lib/legal";

const link = "text-sky-600 font-medium hover:underline";

const CATEGORIES = [
  {
    id: "umum",
    label: "Umum",
    items: [
      { q: "Apa itu RESADA?", a: <>RESADA adalah platform <i>lead generation</i> & <i>sales automation</i> untuk pasar Indonesia. Anda bisa mengumpulkan data bisnis publik (Google Maps, Instagram, Facebook), mengelolanya sebagai lead, lalu menjangkau via WhatsApp & Email Blast — semua dalam satu tempat.</> },
      { q: "Apakah ada paket gratis?", a: <>Ya. Anda bisa mulai gratis untuk mencoba fitur dasar. Untuk volume lebih besar, fitur WA/Email Blast, dan kredit lebih banyak, tersedia paket berbayar di halaman Billing.</> },
      { q: "Untuk siapa RESADA cocok?", a: <>Freelancer, agency, dan UMKM yang ingin menemukan calon klien lebih cepat tanpa riset manual berjam-jam.</> },
    ],
  },
  {
    id: "akun",
    label: "Akun & Langganan",
    items: [
      { q: "Bagaimana cara mendaftar?", a: <>Klik <Link to="/register" className={link}>Daftar Gratis</Link>, buat akun, dan Anda langsung bisa mulai.</> },
      { q: "Bagaimana cara upgrade atau ganti paket?", a: <>Masuk ke menu <b>Billing</b> di dalam aplikasi, pilih paket bulanan/tahunan atau paket kredit, lalu lanjutkan pembayaran.</> },
      { q: "Apakah langganan diperpanjang otomatis?", a: <>Langganan mengikuti siklus yang Anda pilih. Anda dapat mengelola atau menghentikan perpanjangan dari halaman Billing sebelum tanggal tagih berikutnya.</> },
    ],
  },
  {
    id: "pembayaran",
    label: "Pembayaran",
    items: [
      { q: "Metode pembayaran apa yang didukung?", a: <>Pembayaran diproses oleh payment gateway resmi <b>Midtrans</b> dan/atau <b>iPaymu</b>, mendukung kartu, transfer/VA, e-wallet, dan QRIS (tergantung gateway aktif).</> },
      { q: "Apakah data kartu saya aman?", a: <>Ya. Kami <b>tidak menyimpan</b> data kartu Anda. Seluruh proses pembayaran ditangani langsung oleh Midtrans/iPaymu sesuai standar keamanan mereka. Lihat <Link to="/privacy" className={link}>Kebijakan Privasi</Link>.</> },
      { q: "Apa itu kredit dan bagaimana cara kerjanya?", a: <>Kredit adalah satuan kuota untuk menjalankan fitur tertentu seperti scraping. 1 kredit umumnya setara 1 lead yang ditemukan. Kredit bulanan ikut paket; kredit PAYG bersifat rollover sesuai ketentuan.</> },
    ],
  },
  {
    id: "fitur",
    label: "Fitur & Penggunaan",
    items: [
      { q: "Dari mana sumber data lead?", a: <>Dari data bisnis yang tersedia secara publik, mis. Google Maps, Instagram, dan Facebook. Anda bertanggung jawab menggunakan data sesuai hukum yang berlaku.</> },
      { q: "Bagaimana cara mengirim Email Blast?", a: <>Buka halaman <b>Email Blast</b>, hubungkan Gmail Anda lewat <b>App Password</b> (ada tutorialnya di halaman tersebut), gunakan tombol <b>Test Koneksi</b> untuk memastikan valid, lalu pilih lead & template untuk dikirim.</> },
      { q: "Bagaimana cara WhatsApp Blast?", a: <>Hubungkan akun WhatsApp Anda sendiri (BYOK, mis. Fonnte) di halaman Devices, lalu jalankan blast dengan jeda acak & cooldown otomatis untuk menjaga keamanan nomor.</> },
    ],
  },
  {
    id: "teknis",
    label: "Teknis: Gmail App Password",
    items: [
      { q: "Apa itu Gmail App Password?", a: <>App Password adalah sandi 16 karakter khusus aplikasi yang dibuat dari Akun Google Anda. Ini bukan password Gmail biasa, dan wajib mengaktifkan Verifikasi 2 Langkah dulu.</> },
      { q: "Kenapa harus App Password, bukan password biasa?", a: <>Demi keamanan, Google tidak mengizinkan login SMTP dengan password biasa pada akun ber-2FA. App Password lebih aman dan bisa dicabut kapan saja tanpa mengubah password utama.</> },
      { q: "Email tes tidak masuk, kenapa?", a: <>Pastikan Verifikasi 2 Langkah aktif, App Password disalin benar (tanpa spasi), dan email tidak masuk folder Spam. Gunakan tombol <b>Test Koneksi</b> di halaman Email Blast untuk memverifikasi.</> },
    ],
  },
  {
    id: "refund",
    label: "Pengembalian Dana",
    items: [
      { q: "Apakah bisa refund?", a: <>Bisa, sesuai <Link to="/refund" className={link}>Kebijakan Pengembalian Dana</Link>. Singkatnya: pembelian pertama dapat di-refund dalam 7 hari selama pemakaian masih minimal; kredit yang sudah terpakai tidak dapat dikembalikan.</> },
      { q: "Berapa lama proses refund?", a: <>Peninjauan 2–3 hari kerja, lalu pengembalian dana 7–14 hari kerja tergantung bank/penyedia pembayaran.</> },
    ],
  },
  {
    id: "privasi",
    label: "Privasi & Keamanan",
    items: [
      { q: "Apakah kredensial konektor saya aman?", a: <>Ya. Token pihak ketiga dan App Password Gmail Anda disimpan dalam bentuk <b>terenkripsi</b>. Lihat <Link to="/privacy" className={link}>Kebijakan Privasi</Link>.</> },
      { q: "Bagaimana cara menghapus data atau akun saya?", a: <>Hubungi <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a> dan kami akan memproses sesuai UU Pelindungan Data Pribadi.</> },
    ],
  },
];

const TOC = CATEGORIES.map((c) => ({ id: c.id, label: c.label }));

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="faq-question"
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition"
      >
        <span className="font-semibold text-slate-800 text-[15px]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-[15px] leading-relaxed text-slate-600">{a}</div>}
    </div>
  );
}

export default function Faq() {
  return (
    <LegalLayout
      seoTitle={`FAQ — Pertanyaan yang Sering Diajukan | ${COMPANY.brand}`}
      seoDesc={`Pertanyaan umum tentang ${COMPANY.brand}: akun, langganan, pembayaran, fitur scraping & blast, Gmail App Password, refund, dan privasi.`}
      path="/faq"
      title="Pertanyaan yang Sering Diajukan (FAQ)"
      subtitle="Jawaban cepat untuk pertanyaan paling umum seputar akun, pembayaran, fitur, dan keamanan."
      toc={TOC}
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: CATEGORIES.flatMap(cat =>
          cat.items.map(item => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: typeof item.a === 'object' ? item.a.props?.children || '' : String(item.a)
            }
          }))
        )
      }}
    >
      <div className="space-y-10">
        {CATEGORIES.map((cat) => (
          <section key={cat.id} id={cat.id} className="scroll-mt-24">
            <h2 className="font-display text-xl font-bold text-slate-900 mb-4">{cat.label}</h2>
            <div className="space-y-3">
              {cat.items.map((it, i) => <FaqItem key={i} q={it.q} a={it.a} />)}
            </div>
          </section>
        ))}
        <div className="rounded-2xl bg-sky-50 border border-sky-100 p-6">
          <h3 className="font-display font-bold text-slate-800">Masih ada pertanyaan?</h3>
          <p className="text-sm text-slate-600 mt-1">Tim kami siap membantu via email atau WhatsApp.</p>
          <div className="flex flex-wrap gap-3 mt-3 text-sm font-semibold">
            <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a>
            <span className="text-slate-300">·</span>
            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className={link}>WhatsApp {COMPANY.whatsappDisplay}</a>
          </div>
        </div>
      </div>
    </LegalLayout>
  );
}
